import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AppService } from 'src/@core/services/app.service';
import { FunnelService } from './funnel.service';
import { LoopService } from '../loop/loop.service';
import { fieldLabel } from '../loop/loop.logic';
import {
  CostTrendPoint, formatCredits, formatPercent, FunnelMetricsResponse, FunnelMove, readFunnel, roleFor
} from './funnel.logic';
import {
  buildChart, buildInsights, ChangeMarker, Chart, ChartDot, ChartMarker, funnelBands, FunnelBand, Insight,
  shortDate, summariseTrend, targetGap, TrendSummary
} from './funnel-insights.logic';

interface RangeOption {
  label: string;
  days: number;
}

/** What the chart tooltip is showing. */
interface Hover {
  kind: 'point' | 'change';
  left: number; // percent of the chart width
  top: number; // percent of the chart height
  dot?: ChartDot;
  marker?: ChartMarker;
}

/**
 * Funnel tab: how the campaign is performing. A visual funnel with the leads and
 * the credits spent at each stage, plain-language insights, and the cost per goal
 * over time with the changes the loop applied marked on it.
 */
@Component({
  selector: 'hour4u-admin-project-funnel',
  templateUrl: './project-funnel.component.html',
  styleUrls: ['./project-funnel.component.scss']
})
export class ProjectFunnelComponent implements OnInit, OnChanges {
  @Input() projectDetails: any;
  /** Asks the project view to open the project settings (inside project edit). */
  @Output() openSettings = new EventEmitter<void>();

  profile = this.appService.getCurrentAccount();

  ranges: RangeOption[] = [
    { label: '7 days', days: 7 },
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: '1 year', days: 365 }
  ];
  rangeDays = 30;
  /** True once the owner picked a range, so it is never changed for them again. */
  private rangeChosen = false;
  widenedNotice = false;

  data: FunnelMetricsResponse | null = null;
  trend: CostTrendPoint[] = [];
  markers: ChangeMarker[] = [];
  bands: FunnelBand[] = [];
  insights: Insight[] = [];
  chart: Chart | null = null;
  trendSummary: TrendSummary | null = null;
  hover: Hover | null = null;
  isLoading = false;
  loadError = '';
  showTable = false;
  private requestId = 0;

  readonly formatCredits = formatCredits;
  readonly formatPercent = formatPercent;
  readonly roleFor = roleFor;
  readonly shortDate = shortDate;

  constructor(private funnelService: FunnelService, private loopService: LoopService, private appService: AppService) {}

  ngOnInit() {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['projectDetails'];
    if (change && !change.firstChange) {
      const before = change.previousValue;
      const after = change.currentValue;
      // Reload when another project is shown or the funnel was just saved.
      if (!before || !after || before.project_id !== after.project_id ||
        JSON.stringify(before.funnel_config) !== JSON.stringify(after.funnel_config)) {
        this.rangeChosen = false;
        this.load();
      }
    }
  }

  get hasFunnel(): boolean {
    return !!readFunnel(this.projectDetails);
  }

  get target(): number | null {
    const funnel = readFunnel(this.projectDetails);
    return funnel && funnel.target_cost_per_goal ? funnel.target_cost_per_goal : null;
  }

  get summary() {
    return this.data && this.data.summary ? this.data.summary : null;
  }

  get totalLeads(): number {
    return this.summary ? this.summary.total_leads : 0;
  }

  /** Share of all leads that reached the goal, 0..1, or null. */
  get goalRate(): number | null {
    const s = this.summary;
    return s && s.total_leads ? s.goal_leads / s.total_leads : null;
  }

  /** Cost per goal against the target: negative means under target. */
  get gap(): number | null {
    return targetGap(this.summary ? this.summary.cost_per_goal_credits : null, this.target);
  }

  get gapPercent(): number {
    return Math.abs(Math.round((this.gap || 0) * 100));
  }

  get trendChangePercent(): number {
    return this.trendSummary && this.trendSummary.change !== null ? Math.abs(Math.round(this.trendSummary.change * 100)) : 0;
  }

  get hasTrend(): boolean {
    return !!this.chart && this.chart.dots.length > 0;
  }

  get maxStageCredits(): number {
    let max = 0;
    ((this.data && this.data.stages) || []).forEach(s => { max = Math.max(max, s.credits_spent || 0); });
    return max;
  }

  stageCreditShare(credits: number | null | undefined): number {
    const max = this.maxStageCredits;
    return max > 0 ? Math.round(((credits || 0) / max) * 100) : 0;
  }

  setRange(days: number) {
    if (days !== this.rangeDays) {
      this.rangeChosen = true;
      this.widenedNotice = false;
      this.rangeDays = days;
      this.load();
    }
  }

  load() {
    if (!this.projectDetails || !this.projectDetails.project_id || !this.profile || !this.profile.entity) {
      return;
    }
    if (!this.hasFunnel) {
      this.data = null;
      return;
    }
    const id = ++this.requestId;
    this.isLoading = true;
    this.loadError = '';
    const end = new Date();
    const start = new Date(end.getTime() - (this.rangeDays - 1) * 86400000);
    const bucket = this.rangeDays > 90 ? 'week' : 'day';
    const startYmd = this.ymd(start);
    const endYmd = this.ymd(end);
    this.funnelService
      .getMetrics(this.profile.entity.id, this.projectDetails.project_id, startYmd, endYmd, bucket)
      .subscribe(
        (res) => {
          if (id !== this.requestId) { return; }
          // A quiet project: show the last year instead of an empty page.
          if (res && res.configured && !res.rows_read && !this.rangeChosen && this.rangeDays < 365) {
            this.rangeDays = 365;
            this.widenedNotice = true;
            this.load();
            return;
          }
          this.data = res;
          this.isLoading = false;
          this.trend = (res && res.trend) || [];
          this.rebuild(startYmd, endYmd);
          this.loadMarkers(id, startYmd, endYmd);
        },
        () => {
          if (id !== this.requestId) { return; }
          this.loadError = 'Could not load the funnel. Try again.';
          this.isLoading = false;
        }
      );
  }

  /** Changes the loop applied, so they can be marked on the chart. A failure here only hides the markers. */
  private loadMarkers(id: number, startYmd: string, endYmd: string) {
    this.loopService.getRuns(this.profile.entity.id, this.projectDetails.project_id).subscribe(
      (res) => {
        if (id !== this.requestId) { return; }
        const markers: ChangeMarker[] = [];
        ((res && res.runs) || []).forEach(run => {
          (run.applied_changes || []).forEach(c => {
            if (c.applied_at) {
              markers.push({
                at: c.applied_at,
                label: fieldLabel(c.field),
                why: c.rationale || c.expected_effect || '',
                runId: run.run_id
              });
            }
          });
        });
        markers.sort((a, b) => a.at - b.at);
        this.markers = markers;
        this.rebuild(startYmd, endYmd);
      },
      () => { /* markers are optional */ }
    );
  }

  private rebuild(startYmd: string, endYmd: string) {
    const data = this.data;
    this.bands = data && data.stages ? funnelBands(data.stages, data.moves || []) : [];
    this.insights = buildInsights(data, this.target, this.trend);
    this.trendSummary = summariseTrend(this.trend);
    this.chart = buildChart(this.trend, this.target, this.markers, startYmd, endYmd);
    this.hover = null;
  }

  showPoint(dot: ChartDot) {
    if (!this.chart) { return; }
    this.hover = {
      kind: 'point', dot,
      left: (dot.x / this.chart.width) * 100,
      top: (dot.y / this.chart.height) * 100
    };
  }

  showChange(marker: ChartMarker) {
    if (!this.chart) { return; }
    this.hover = { kind: 'change', marker, left: (marker.x / this.chart.width) * 100, top: 12 };
  }

  hideHover() {
    this.hover = null;
  }

  channelLabel(move: FunnelMove): string {
    if (move.recommended_channel === 'whatsapp') { return 'WhatsApp is cheaper'; }
    if (move.recommended_channel === 'voice') { return 'Voice'; }
    return '–';
  }

  trackById(_: number, item: { id: string }) {
    return item.id;
  }

  trackByIndex(i: number) {
    return i;
  }

  /** Dates are sent as IST calendar days, whatever the viewer's time zone. */
  private ymd(d: Date): string {
    const ist = new Date(d.getTime() + 330 * 60000);
    return ist.toISOString().slice(0, 10);
  }
}
